import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ChevronRight, Baby, Sparkles, Stethoscope, HeartPulse, Flower2, HandHeart, Phone } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { Reveal, StaggerGroup, StaggerItem, GlowCard, AnimatedLine, FloatingBlob, Magnetic } from "@/components/Motion";
import { services } from "@/lib/data";
import { SITE } from "@/lib/api";
import SEO from "@/components/SEO";

const iconMap = { Baby, Sparkles, Stethoscope, HeartPulse, Flower2, HandHeart };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const I = iconMap[service.icon] || HeartPulse;
  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`service-detail-${slug}`} className="relative overflow-hidden">
      <SEO title={service.title} description={service.short} image={service.image} />
      <FloatingBlob color="aurora-purple" size={400} top="-5%" right="-10%" delay={0} />

      <section className="py-12 lg:py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link to="/services" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-coral)] inline-flex items-center gap-2 transition-colors" data-testid="service-back-link">
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
        </div>
      </section>

      <section className="pb-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 rounded-xl bg-[var(--color-coral-light)] border border-[var(--color-coral)]/20 shadow-[0_0_15px_var(--color-coral-glow)] flex items-center justify-center">
                <I className="w-6 h-6 text-[var(--color-coral)]" strokeWidth={1.5} />
              </span>
              <span className="overline">Service</span>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05]">{service.title}</h1>
            <p className="mt-6 text-lg text-[var(--color-text-muted)] leading-relaxed max-w-2xl">{service.short}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Magnetic><Link to="/appointment" data-testid="service-detail-book" className="btn-primary text-sm">Book Consultation <ArrowRight className="w-4 h-4" /></Link></Magnetic>
              <a href={`tel:${SITE.phone}`} className="btn-outline text-sm"><Phone className="w-4 h-4" /> {SITE.phoneDisplay}</a>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="img-hover-scale rounded-2xl border border-[var(--color-border-2)]">
              <Placeholder variant="tall" label={service.title} src={service.image} />
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      <section className="py-16 relative bg-[var(--color-surface)]">
        <FloatingBlob color="aurora-warm" size={350} top="20%" left="-5%" delay={1} />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-6 relative">
          <Reveal>
            <div className="overline">Overview</div>
          </Reveal>
          <StaggerGroup stagger={0.1}>
            {service.long.map((p, i) => (
              <StaggerItem key={i}>
                <p className="text-lg md:text-xl text-[var(--color-text-muted)] leading-relaxed font-heading">{p}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      <section className="py-16 lg:py-24 relative">
        <FloatingBlob color="aurora-coral" size={400} bottom="10%" right="5%" delay={2} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <Reveal>
            <div className="overline mb-4">What&apos;s included</div>
            <h2 className="font-heading text-3xl md:text-4xl text-[var(--color-text)] mb-10 max-w-2xl">Care that&apos;s thorough — never rushed.</h2>
          </Reveal>
          <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.1}>
            {service.highlights.map((h, i) => (
              <StaggerItem key={h}>
                <GlowCard className="p-6 h-full">
                  <div className="text-xs text-[var(--color-coral)] font-semibold uppercase tracking-wider mb-2">0{i + 1}</div>
                  <div className="font-heading text-xl text-white">{h}</div>
                </GlowCard>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[var(--color-surface)] relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <h2 className="font-heading text-3xl md:text-4xl text-[var(--color-text)]">Other services</h2>
              <Link to="/services" className="btn-ghost text-sm">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
          <StaggerGroup className="grid md:grid-cols-3 gap-6" stagger={0.1}>
            {others.map((s) => {
              const Oi = iconMap[s.icon] || HeartPulse;
              return (
                <StaggerItem key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="group glass-card block p-7 h-full border border-[var(--color-border)] hover:border-[var(--color-coral)] transition-colors"
                    data-testid={`other-service-${s.slug}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[var(--color-coral-light)] flex items-center justify-center mb-4 border border-[var(--color-coral)]/20">
                      <Oi className="w-4 h-4 text-[var(--color-coral)]" />
                    </div>
                    <h3 className="font-heading text-xl text-white group-hover:text-[var(--color-coral)] transition-colors">{s.title}</h3>
                    <p className="text-sm text-white/80 mt-2 leading-relaxed">{s.short}</p>
                    <div className="mt-4 text-xs uppercase tracking-wider text-[var(--color-coral)] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read more <ChevronRight className="w-3 h-3" />
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
}
